import type { GraphNode } from './graph';

/**
 * The player's anim graph. Locomotion blend anchors are the clips' MEASURED slide-free stride
 * speeds (forensics 2026-08-26) — the pose matches the gait that actually produces that foot
 * speed at ~1× rate, and the phase-sync bridge (characterView + anim/phaseSync) owns playback
 * so foot speed always equals ground speed. Above 4.094 m/s run_boost holds and rate-scales
 * (up to ~1.7× at 7 m/s — energetic, on-style).
 *
 * Roll uses the land clip PINNED at its deepest crouch (t = 0.4167, hips 0.412 m) as a tuck
 * pose under the procedural wrapper spin — A_Dodging_Right measured as an in-place lateral
 * lean (7 cm sway, feet never leave ground); unusable, dropped.
 */
export const ROLL_TUCK_CLIP = 'land';

/**
 * The roll ANIMATION exits at this progress (spin is complete): the last stretch of the
 * mechanic crossfades into locomotion, whose poses carry the arms naturally at the sides —
 * the land clip's standup tail was throwing them out for balance (it's a jump-landing
 * recovery), and no IK pin reads cleaner than simply not playing it.
 */
export const ROLL_ANIM_EXIT = 0.72;

/**
 * Roll pose timeline: extended reach (0.27 s of the land clip) → deepest tuck (0.4167 s) held
 * through the tumble → a slight rise as the spin finishes, then the graph exits to locomotion.
 */
export function rollTuckTime(progress01: number): number {
  const REACH = 0.27;
  const TUCK = 0.4167;
  const RISE = 0.6;
  if (progress01 < 0.22) return REACH + (TUCK - REACH) * (progress01 / 0.22);
  if (progress01 < 0.6) return TUCK;
  const t = Math.min((progress01 - 0.6) / (ROLL_ANIM_EXIT - 0.6), 1);
  return TUCK + (RISE - TUCK) * t;
}

/** Wrapper spin angle (rad): delayed start (the dive lean reads first), done by 72% progress. */
export function rollSpinAngle(progress01: number): number {
  const t = Math.min(Math.max((progress01 - 0.08) / 0.64, 0), 1);
  const eased = t * t * (3 - 2 * t);
  return eased * Math.PI * 2;
}

/**
 * Hand-reach blend during the dive: strong and held through the whole dive window, releasing
 * as the tumble inverts the body (the IK plants hands toward the ground ahead).
 */
export function rollReachBlend(progress01: number): number {
  const up = Math.min(progress01 / 0.06, 1);
  const down = Math.min(Math.max((0.42 - progress01) / 0.12, 0), 1);
  return 0.9 * Math.min(up, down);
}

/** Smooth 0→1→0 pulse centered at `c` with half-width `w`. */
function pulse(p: number, c: number, w: number): number {
  const x = (p - c) / w;
  if (x <= -1 || x >= 1) return 0;
  const s = Math.cos((x * Math.PI) / 2);
  return s * s;
}

/**
 * Dive-over-the-ground shape: the body's visual center DROPS toward the ground through the
 * tumble (a dive plants low — a fixed-height spin reads as a coin flip)…
 */
export function rollDip(progress01: number): number {
  return 0.3 * pulse(progress01, 0.4, 0.33); // settled by the anim exit (~0.72)
}

/** …and LEADS forward of the capsule during the dive, settling back by the recovery. */
export function rollLead(progress01: number): number {
  return 0.34 * pulse(progress01, 0.26, 0.3);
}


export const PLAYER_GRAPH: GraphNode = {
  kind: 'select',
  choose: (p) => {
    if (p.rolling) return 'roll';
    if (p.grounded) return 'locomotion';
    if (p.hovering) return 'hover';
    return p.vy > 0 ? 'rise' : 'fall';
  },
  fades: { roll: 0.05, rise: 0.04, fall: 0.12, hover: 0.18, locomotion: 0.1 },
  children: {
    locomotion: {
      kind: 'blend1d',
      param: (p) => p.speed,
      points: [
        { at: 0, node: { kind: 'clip', clip: 'idle' } },
        { at: 1.122, node: { kind: 'clip', clip: 'walk' } },
        { at: 1.845, node: { kind: 'clip', clip: 'run' } },
        { at: 4.094, node: { kind: 'clip', clip: 'run_boost' } },
      ],
    },
    // Standing vs in-motion jump/fall picked by horizontal speed; small blend zone avoids pops.
    // Tier-0 upgrades: 'rise' (A_Jumping_Up) and 'fall_loop' (A_Falling) replace the trimmed
    // Action-figure standing set — measured cleaner (fall_loop wrap 0.1°, rise fits apex at 1×).
    rise: {
      kind: 'blend1d',
      param: (p) => p.speed,
      points: [
        { at: 2.5, node: { kind: 'clip', clip: 'rise' } },
        { at: 4.0, node: { kind: 'clip', clip: 'jump_run_start' } },
      ],
    },
    fall: {
      kind: 'blend1d',
      param: (p) => p.speed,
      points: [
        { at: 2.5, node: { kind: 'clip', clip: 'fall_loop' } },
        { at: 4.0, node: { kind: 'clip', clip: 'fall_run' } },
      ],
    },
    hover: { kind: 'clip', clip: 'float' },
    roll: { kind: 'clip', clip: ROLL_TUCK_CLIP },
  },
};
